/**
 * RichLesson — Renders a structured lesson (rich_content JSON) with typed sections.
 *
 * Section kinds supported:
 *   - text         { title, body }
 *   - list         { title, items[] }
 *   - stat_block   { label, value, context }
 *   - step         { number, title, body, example }
 *   - do_dont      { do[], dont[] }
 *   - warning_block{ title, body, items[], rule }
 *   - key_phrase   { phrase }
 *   - gift_levels_dynamic { title, fallback_intro }   ← Iter-27.17 (auto-fetches assos)
 *
 * The component is intentionally self-contained — no external markdown lib —
 * so the rendering stays predictable and on-brand (Shariti dark theme).
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
    BookOpen,
    Sparkles,
    CheckCircle2,
    XCircle,
    AlertTriangle,
    Quote,
    Target,
    Banknote,
    ChevronDown,
} from "lucide-react";
import api from "../lib/api";

function StatBlock({ label, value, context }) {
    return (
        <div
            className="rounded-2xl bg-gradient-to-br from-shariti-orange/15 to-shariti-yellow/5 border border-shariti-orange/30 p-5 sm:p-6"
            data-testid="rich-stat-block"
        >
            <div className="flex items-baseline gap-3 mb-1.5 flex-wrap">
                <Target className="w-5 h-5 text-shariti-orange shrink-0" />
                <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light">
                    {label}
                </p>
            </div>
            <p className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-1.5">
                {value}
            </p>
            <p className="text-sm text-white/65 leading-relaxed">{context}</p>
        </div>
    );
}

function TextSection({ title, body }) {
    return (
        <div className="space-y-2" data-testid="rich-text-section">
            {title && (
                <h3 className="text-lg sm:text-xl font-extrabold text-white tracking-tight">
                    {title}
                </h3>
            )}
            <p
                className="text-white/75 leading-relaxed text-sm sm:text-base font-light"
                dangerouslySetInnerHTML={{ __html: applyInlineFormatting(body) }}
            />
        </div>
    );
}

function ListSection({ title, items }) {
    return (
        <div className="space-y-2" data-testid="rich-list-section">
            {title && (
                <h3 className="text-lg font-extrabold text-white tracking-tight">{title}</h3>
            )}
            <ul className="space-y-1.5 text-sm text-white/75 leading-relaxed">
                {items.map((it, i) => (
                    <li key={i} className="flex gap-2">
                        <span className="text-shariti-orange shrink-0 mt-1">•</span>
                        <span dangerouslySetInnerHTML={{ __html: applyInlineFormatting(it) }} />
                    </li>
                ))}
            </ul>
        </div>
    );
}

function StepSection({ number, title, body, example, total = 9 }) {
    return (
        <div className="glass-card rounded-2xl p-5 sm:p-6" data-testid={`rich-step-${number}`}>
            <div className="flex items-start gap-4 mb-3">
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl bg-shariti-orange flex items-center justify-center font-extrabold text-white text-base sm:text-lg shrink-0">
                    {number}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-orange-light mb-0.5">
                        Étape {number} / {total}
                    </p>
                    <h3 className="text-lg sm:text-xl font-extrabold text-white tracking-tight">
                        {title}
                    </h3>
                </div>
            </div>
            <p
                className="text-white/75 leading-relaxed text-sm sm:text-base font-light mb-3"
                dangerouslySetInnerHTML={{ __html: applyInlineFormatting(body) }}
            />
            {example && (
                <div className="rounded-xl bg-shariti-yellow/8 border border-shariti-yellow/20 p-3.5">
                    <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-yellow mb-1">
                        Exemple terrain
                    </p>
                    <p className="text-sm text-white/85 italic leading-relaxed">{example}</p>
                </div>
            )}
        </div>
    );
}

function DoDontSection({ do: doItems, dont: dontItems }) {
    return (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="rich-do-dont">
            <div className="rounded-2xl bg-emerald-500/8 border border-emerald-500/25 p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    <p className="text-[11px] font-extrabold tracking-widest uppercase text-emerald-300">
                        À faire
                    </p>
                </div>
                <ul className="space-y-2 text-sm text-white/80 leading-relaxed">
                    {doItems.map((it, i) => (
                        <li key={i} className="flex gap-2">
                            <span className="text-emerald-400 shrink-0 mt-0.5">+</span>
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: applyInlineFormatting(it),
                                }}
                            />
                        </li>
                    ))}
                </ul>
            </div>
            <div className="rounded-2xl bg-red-500/8 border border-red-500/25 p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-3">
                    <XCircle className="w-5 h-5 text-red-400" />
                    <p className="text-[11px] font-extrabold tracking-widest uppercase text-red-300">
                        À éviter
                    </p>
                </div>
                <ul className="space-y-2 text-sm text-white/80 leading-relaxed">
                    {dontItems.map((it, i) => (
                        <li key={i} className="flex gap-2">
                            <span className="text-red-400 shrink-0 mt-0.5">−</span>
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: applyInlineFormatting(it),
                                }}
                            />
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

function WarningBlock({ title, body, items, rule }) {
    return (
        <div
            className="rounded-2xl bg-shariti-yellow/8 border-l-4 border border-shariti-yellow/30 p-4 sm:p-5"
            data-testid="rich-warning-block"
        >
            <div className="flex items-start gap-3 mb-2">
                <AlertTriangle className="w-5 h-5 text-shariti-yellow shrink-0 mt-0.5" />
                <h3 className="text-base sm:text-lg font-extrabold text-white tracking-tight">
                    {title}
                </h3>
            </div>
            {body && (
                <p
                    className="text-sm text-white/75 leading-relaxed mb-3 ml-8"
                    dangerouslySetInnerHTML={{ __html: applyInlineFormatting(body) }}
                />
            )}
            {items && items.length > 0 && (
                <ul className="space-y-1.5 text-sm text-white/80 leading-relaxed ml-8 mb-3">
                    {items.map((it, i) => (
                        <li key={i} className="flex gap-2">
                            <span className="text-shariti-yellow shrink-0 mt-1">▸</span>
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: applyInlineFormatting(it),
                                }}
                            />
                        </li>
                    ))}
                </ul>
            )}
            {rule && (
                <div className="ml-8 pt-3 border-t border-white/10">
                    <p
                        className="text-xs font-bold tracking-wide text-white/85 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: applyInlineFormatting(rule) }}
                    />
                </div>
            )}
        </div>
    );
}

function KeyPhrase({ phrase }) {
    return (
        <div
            className="rounded-2xl bg-gradient-to-br from-shariti-orange/12 to-shariti-yellow/4 border border-shariti-orange/25 p-5 sm:p-6 text-center"
            data-testid="rich-key-phrase"
        >
            <Quote className="w-6 h-6 text-shariti-orange-light mx-auto mb-2.5" />
            <p
                className="text-base sm:text-lg font-extrabold text-white leading-snug italic"
                dangerouslySetInnerHTML={{ __html: applyInlineFormatting(phrase) }}
            />
        </div>
    );
}

function GiftLevelsDynamic({ title, fallback_intro }) {
    const [associations, setAssociations] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/associations");
                // Filter to assos with gift_levels for cleaner UX
                const withLevels = data.filter(
                    (a) => Array.isArray(a.gift_levels) && a.gift_levels.length > 0,
                );
                // Iter-27.20 — sort: my assigned assos first, then alphabetical
                withLevels.sort((a, b) => {
                    const am = a.is_my_asso ? 0 : 1;
                    const bm = b.is_my_asso ? 0 : 1;
                    if (am !== bm) return am - bm;
                    return (a.name || "").localeCompare(b.name || "");
                });
                setAssociations(withLevels);
                if (withLevels.length > 0) setSelectedId(withLevels[0].association_id);
            } catch (e) {
                // Fail silently — we still render the fallback.
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const selected = associations.find((a) => a.association_id === selectedId);
    const giftLevels = selected?.gift_levels || [];
    const otherAssos = associations.filter((a) => a.association_id !== selectedId);
    const isMyAsso = (a) => Boolean(a?.is_my_asso);
    const hasMultiple = associations.length > 1;
    const hasOnlyOne = associations.length === 1;

    return (
        <div
            className="rounded-2xl glass-card p-5 sm:p-6 space-y-4"
            data-testid="rich-gift-levels"
        >
            <div className="flex items-center gap-2.5 flex-wrap">
                <Banknote className="w-5 h-5 text-shariti-orange" />
                <h3 className="text-lg font-extrabold text-white tracking-tight flex-1 min-w-0">
                    {title}
                </h3>
            </div>

            {/* Iter-27.20 — Disclaimer adaptatif au contexte réel de l'apprenant */}
            {!loading && hasMultiple && (
                <div className="rounded-xl bg-shariti-yellow/8 border border-shariti-yellow/25 p-3.5">
                    <p className="text-xs text-white/80 leading-relaxed">
                        <span className="font-extrabold text-shariti-yellow">⚠ Important — </span>
                        Les paliers{" "}
                        <span className="font-bold text-white">
                            varient d'une association à l'autre
                        </span>
                        . Tu as accès à{" "}
                        <span className="font-bold text-white">
                            {associations.length} associations
                        </span>{" "}
                        : utilise le sélecteur ci-dessous pour basculer entre elles.
                    </p>
                </div>
            )}
            {!loading && hasOnlyOne && (
                <div className="rounded-xl bg-shariti-orange/8 border border-shariti-orange/20 p-3.5">
                    <p className="text-xs text-white/80 leading-relaxed">
                        <span className="font-extrabold text-shariti-orange-light">
                            Tes paliers terrain —{" "}
                        </span>
                        Voici les équivalences configurées pour{" "}
                        <span className="font-bold text-white">{selected?.name}</span>, l'association
                        sur laquelle tu es positionné·e. Apprends-les par cœur, c'est ce que tu
                        raconteras à chaque passant.
                    </p>
                </div>
            )}

            {hasMultiple && (
                <div className="relative" data-testid="gift-levels-asso-picker">
                    <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-white/45 mb-1.5">
                        Choisis ton association
                    </p>
                    <button
                        onClick={() => setOpen(!open)}
                        className="w-full flex items-center justify-between gap-2 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white hover:border-shariti-orange/40 transition-all"
                    >
                        <span className="text-sm font-bold text-shariti-orange-light flex items-center gap-2">
                            {selected?.name || "—"}
                            {selected && isMyAsso(selected) && (
                                <span className="inline-flex items-center gap-1 bg-emerald-500/15 border border-emerald-500/30 rounded-full px-2 py-0.5 text-[9px] font-extrabold tracking-wider uppercase text-emerald-300">
                                    Ton asso
                                </span>
                            )}
                        </span>
                        <ChevronDown
                            className={`w-4 h-4 text-white/55 transition-transform ${
                                open ? "rotate-180" : ""
                            }`}
                        />
                    </button>
                    {open && (
                        <div className="absolute top-full mt-2 w-full bg-shariti-dark border border-white/15 rounded-xl shadow-2xl z-20 max-h-64 overflow-y-auto">
                            {associations.map((a) => (
                                <button
                                    key={a.association_id}
                                    onClick={() => {
                                        setSelectedId(a.association_id);
                                        setOpen(false);
                                    }}
                                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-shariti-orange/10 transition-colors flex items-center justify-between gap-2 ${
                                        a.association_id === selectedId
                                            ? "bg-shariti-orange/15 text-shariti-orange-light"
                                            : "text-white/80"
                                    }`}
                                >
                                    <span>{a.name}</span>
                                    {isMyAsso(a) && (
                                        <span className="inline-flex items-center gap-1 bg-emerald-500/15 border border-emerald-500/30 rounded-full px-2 py-0.5 text-[9px] font-extrabold tracking-wider uppercase text-emerald-300 shrink-0">
                                            Ton asso
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {!loading && giftLevels.length > 0 ? (
                <>
                    {/* Iter-27.19 — Mention explicite du nom de l'asso pour lever toute ambiguïté */}
                    {hasMultiple && (
                        <div className="flex items-center gap-2 flex-wrap pt-1">
                            <span className="text-[10px] font-thin tracking-[0.25em] uppercase text-white/45">
                                Paliers configurés pour
                            </span>
                            <span className="inline-flex items-center gap-1.5 bg-shariti-orange/15 border border-shariti-orange/30 rounded-full px-3 py-0.5 text-xs font-bold text-shariti-orange-light">
                                {selected?.name}
                            </span>
                        </div>
                    )}

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                        {giftLevels.map((g, i) => (
                            <div
                                key={i}
                                className="rounded-xl bg-shariti-orange/8 border border-shariti-orange/20 p-3.5"
                            >
                                <p className="text-2xl font-extrabold text-shariti-orange-light mb-1">
                                    {g.amount} €/mois
                                </p>
                                {g.label && (
                                    <p className="text-[10px] font-thin tracking-widest uppercase text-white/50 mb-1.5">
                                        {g.label}
                                    </p>
                                )}
                                <p className="text-xs text-white/70 leading-relaxed">{g.impact}</p>
                            </div>
                        ))}
                    </div>

                    {/* Iter-27.19 — Comparateur déplié : voir toutes les autres assos */}
                    {otherAssos.length > 0 && (
                        <div className="pt-2">
                            <button
                                type="button"
                                onClick={() => setShowAll(!showAll)}
                                data-testid="gift-levels-toggle-all"
                                className="text-xs font-bold text-shariti-orange-light hover:text-shariti-orange transition-colors inline-flex items-center gap-1.5"
                            >
                                <ChevronDown
                                    className={`w-3.5 h-3.5 transition-transform ${
                                        showAll ? "rotate-180" : ""
                                    }`}
                                />
                                {showAll ? "Masquer" : "Voir"} les{" "}
                                {otherAssos.length} autre{otherAssos.length > 1 ? "s" : ""}{" "}
                                association{otherAssos.length > 1 ? "s" : ""} configurée
                                {otherAssos.length > 1 ? "s" : ""}
                            </button>
                            {showAll && (
                                <div className="mt-3 space-y-3" data-testid="gift-levels-all">
                                    {otherAssos.map((a) => (
                                        <div
                                            key={a.association_id}
                                            className="rounded-xl bg-white/3 border border-white/8 p-3.5 space-y-2"
                                        >
                                            <div className="flex items-center gap-2">
                                                <p className="text-xs font-bold text-white/85">
                                                    {a.name}
                                                </p>
                                                {isMyAsso(a) && (
                                                    <span className="inline-flex items-center gap-1 bg-emerald-500/15 border border-emerald-500/30 rounded-full px-2 py-0.5 text-[9px] font-extrabold tracking-wider uppercase text-emerald-300">
                                                        Ton asso
                                                    </span>
                                                )}
                                            </div>
                                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                                                {a.gift_levels.map((g, i) => (
                                                    <div
                                                        key={i}
                                                        className="rounded-lg bg-white/4 border border-white/8 p-2.5"
                                                    >
                                                        <p className="text-base font-extrabold text-white mb-0.5">
                                                            {g.amount} €/mois
                                                        </p>
                                                        {g.label && (
                                                            <p className="text-[9px] font-thin tracking-wider uppercase text-white/45 mb-1">
                                                                {g.label}
                                                            </p>
                                                        )}
                                                        <p className="text-[11px] text-white/60 leading-snug">
                                                            {g.impact}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </>
            ) : !loading ? (
                <>
                    <p className="text-sm text-white/65 leading-relaxed">
                        {fallback_intro ||
                            "Tes paliers seront affichés ici dès qu'ils seront configurés. Pas de panique, tu pourras commencer la formation sans."}
                    </p>
                </>
            ) : (
                <div className="h-32 rounded-xl bg-white/3 animate-pulse" />
            )}
        </div>
    );
}

/**
 * Apply inline formatting:
 *   **bold** → <strong>bold</strong>
 *   *italic* → <em>italic</em>
 *   `code`   → <code>code</code>
 * Returns escaped HTML safe to inject.
 */
function applyInlineFormatting(text = "") {
    return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\*\*([^*]+)\*\*/g, "<strong class='text-white font-extrabold'>$1</strong>")
        .replace(/\*([^*]+)\*/g, "<em class='text-shariti-orange-light'>$1</em>")
        .replace(
            /`([^`]+)`/g,
            "<code class='bg-white/8 px-1.5 py-0.5 rounded text-shariti-yellow font-mono text-[0.9em]'>$1</code>",
        );
}

export default function RichLesson({ rich_content, gift_levels, association_name }) {
    if (!rich_content) return null;
    const { intro, sections = [], punchline } = rich_content;

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-5"
            data-testid="rich-lesson"
        >
            {intro && (
                <div className="rounded-2xl bg-white/3 border-l-2 border-shariti-orange/50 p-4 sm:p-5">
                    <div className="flex items-start gap-2.5">
                        <BookOpen className="w-5 h-5 text-shariti-orange-light shrink-0 mt-0.5" />
                        <p
                            className="text-white/85 leading-relaxed text-base sm:text-lg font-light"
                            dangerouslySetInnerHTML={{
                                __html: applyInlineFormatting(intro),
                            }}
                        />
                    </div>
                </div>
            )}

            {sections.map((s, i) => {
                switch (s.kind) {
                    case "text":
                        return <TextSection key={i} title={s.title} body={s.body} />;
                    case "list":
                        return <ListSection key={i} title={s.title} items={s.items || []} />;
                    case "stat_block":
                        return (
                            <StatBlock
                                key={i}
                                label={s.label}
                                value={s.value}
                                context={s.context}
                            />
                        );
                    case "step": {
                        // Iter-27.22 — count total steps in the lesson so the
                        // counter "Étape N / total" is always correct.
                        const totalSteps = sections.filter((x) => x.kind === "step").length;
                        return (
                            <StepSection
                                key={i}
                                number={s.number}
                                title={s.title}
                                body={s.body}
                                example={s.example}
                                total={totalSteps}
                            />
                        );
                    }
                    case "do_dont":
                        return <DoDontSection key={i} do={s.do || []} dont={s.dont || []} />;
                    case "warning_block":
                        return (
                            <WarningBlock
                                key={i}
                                title={s.title}
                                body={s.body}
                                items={s.items || []}
                                rule={s.rule}
                            />
                        );
                    case "key_phrase":
                        return <KeyPhrase key={i} phrase={s.phrase} />;
                    case "gift_levels_dynamic":
                        return (
                            <GiftLevelsDynamic
                                key={i}
                                title={s.title}
                                fallback_intro={s.fallback_intro}
                            />
                        );
                    default:
                        return null;
                }
            })}

            {punchline && (
                <div className="rounded-2xl bg-gradient-to-br from-shariti-orange to-shariti-orange-light p-4 sm:p-5">
                    <div className="flex items-start gap-3">
                        <Sparkles className="w-5 h-5 text-white shrink-0 mt-0.5" />
                        <p className="text-white font-bold text-sm sm:text-base leading-relaxed">
                            {punchline}
                        </p>
                    </div>
                </div>
            )}
        </motion.div>
    );
}
